/**
 * src/stores/useSyncStore.ts
 *
 * Non-persisted Zustand store for sync status state machine (CLOUD-03, CLOUD-05).
 *
 * NON-PERSISTED: status is ephemeral (resets on page load). The queue itself
 * lives in IDB (src/shared/lib/changeQueue.ts), not here.
 * Pattern: useAuthStore shape (no persist() wrapper — ephemeral state only).
 *
 * State machine:
 *   idle      → flush() with empty queue → idle (no-op)
 *   idle      → probe fails             → offline
 *   idle      → probe ok + records      → syncing → synced | error
 *   offline   → probe ok + records      → syncing → synced | error
 *   syncing   → 200 response            → synced
 *   syncing   → non-200 / throw         → error
 *
 * Security: T-04-09 (captive portal) mitigated by HEAD probe before flush.
 * T-04-12 (lost edits): dequeueFlushed only called AFTER 200 response per chunk.
 *
 * Background only: never opens modals, never blocks the editor.
 */

import { create } from 'zustand';
import { peekQueue, dequeueFlushed } from '@/shared/lib/changeQueue';
import { mergeProjectsFromServer } from '@/shared/lib/conflictResolver';
import { selectFlushedIds } from '@/shared/lib/syncProtocol';
import { useProjectStore } from '@/stores/useProjectStore';
import { useUpgradeModal } from '@/features/billing';
import type { SyncStatus } from '@/entities/sync/model/types';
import type { z } from 'zod';
import { SyncBatchResponseSchema } from '@/shared/validation/schemas';
import type { ProjectSnapshotSchema } from '@/shared/validation/schemas';

type ProjectSnapshot = z.infer<typeof ProjectSnapshotSchema>;

// Chunk size for POST /api/sync/batch (D1 batch budget limit is 50; we use 40)
const BATCH_CHUNK_SIZE = 40;

interface SyncState {
  status: SyncStatus;
  lastSyncedAt: number | null;
  pendingCount: number;
  /** Count of change records the server's `rejected` list carried for the most
   *  recent flush cycle (CR-01). Reset to 0 by setStatus for every status
   *  EXCEPT 'conflict' (WR-05 fix): a repair/migration conflict signal must not
   *  silently erase a genuine rejection count set earlier in the same session. */
  rejectedCount: number;
  error: string | null;
  /**
   * True from the moment syncAndHydrate()'s hydration step (SYNC-03, Phase 36 plan 05)
   * begins until it settles, on both the success and failure paths (src/features/sync/lib/hydrate.ts
   * sets this in a try/finally). Consumed by plan 36-07's dashboard loading signal
   * (UI-SPEC backstop row 8) — never surfaced as a spinner (this store stays background-only).
   */
  hydrating: boolean;

  setStatus: (status: SyncStatus, at?: number) => void;
  setPendingCount: (n: number) => void;
  setError: (msg: string | null) => void;
  setHydrating: (v: boolean) => void;

  /**
   * Flush the SyncQueue to the server.
   * 1. If queue is empty: no-op (never transitions to 'syncing').
   * 2. HEAD-probe /api/health: if fails, set 'offline', retain queue.
   * 3. chunk records at 40; POST each chunk to /api/sync/batch.
   * 4. On 200: apply mergeProjectsFromServer, dequeue chunk ids.
   * 5. On error: set 'error', retain remaining queue.
   * Background only — never blocks UI.
   */
  flush: () => Promise<void>;
}

/**
 * Lightweight connectivity probe.
 * Returns false on captive portal / LAN without internet (T-04-09 mitigation).
 */
async function isReallyOnline(): Promise<boolean> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return false;
  try {
    const res = await fetch(`/api/health?r=${Math.random()}`, {
      method: 'HEAD',
      cache: 'no-store',
      signal: AbortSignal.timeout(3000),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export const useSyncStore = create<SyncState>()((set, _get) => ({
  // Guard navigator.onLine for SSR/non-browser environments
  status: typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'idle',
  lastSyncedAt: null,
  pendingCount: 0,
  rejectedCount: 0,
  error: null,
  hydrating: false,

  setStatus: (status, at) =>
    // WR-05 fix: a transition TO 'conflict' (first-sync repair conflict —
    // src/features/sync/lib/firstSync.ts's repairPut — or the guest-migration
    // conflict badge in src/stores/migrate.ts) must not silently erase a
    // rejectedCount/error signal that flush() already set earlier in the
    // same syncAndHydrate() cycle: 'conflict' and 'N record(s) rejected by
    // the server' are not mutually exclusive facts about the same sync
    // session, and the rejected records stay queued regardless of what the
    // status pill shows. Every OTHER status keeps the existing "clean
    // slate" contract (both callers below have relied on setStatus('idle')
    // clearing rejectedCount/error since WR-02). Omitting error/rejectedCount
    // from the object below (instead of reading previous state) is enough —
    // zustand's set() shallow-merges, so untouched keys keep their value.
    set({
      status,
      lastSyncedAt: at ?? null,
      ...(status === 'conflict' ? {} : { error: null, rejectedCount: 0 }),
    }),

  setPendingCount: (n) => set({ pendingCount: n }),

  setError: (msg) => set({ status: 'error', error: msg }),

  setHydrating: (v) => set({ hydrating: v }),

  flush: async () => {
    // 1. Peek at the queue — if empty, no-op
    const queue = await peekQueue();
    if (queue.length === 0) {
      return;
    }
    set({ pendingCount: queue.length });

    // 2. Probe connectivity — captive portal mitigation
    const online = await isReallyOnline();
    if (!online) {
      set({ status: 'offline' });
      return;
    }

    // 3. Begin sync
    set({ status: 'syncing' });

    // CR-01: accumulate rejected entries across every chunk in this flush cycle —
    // a rejection in an earlier chunk must not be forgotten once a later chunk succeeds.
    const allRejected: { id: string; reason: string }[] = [];

    try {
      // Chunk the queue at 40 records per batch (D1 budget)
      for (let i = 0; i < queue.length; i += BATCH_CHUNK_SIZE) {
        const chunk = queue.slice(i, i + BATCH_CHUNK_SIZE);

        const res = await fetch('/api/sync/batch', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ changes: chunk }),
        });

        if (res.status === 402) {
          // Pro plan required — surface the upgrade prompt, retain queue (T-08-20)
          // Do NOT import React; call store via getState() from non-React code
          useUpgradeModal.getState().openUpgrade('sync');
          // WR-02 fix: an earlier chunk's rejections must not be silently dropped
          // just because a LATER chunk in this same cycle hit a 402 — surface the
          // accumulated rejectedCount/error before the early return.
          set({
            status: 'idle',
            ...(allRejected.length > 0
              ? { rejectedCount: allRejected.length, error: `${allRejected.length} change(s) rejected by server` }
              : {}),
          });
          return;
        }

        if (!res.ok) {
          // Server error — stop flushing, retain queue.
          // WR-02 fix: same rationale as the 402 branch above — do not let a
          // later chunk's transient server error erase an earlier chunk's real
          // rejectedCount signal.
          set({
            status: 'error',
            error: allRejected.length > 0
              ? `Sync failed: ${res.status} (${allRejected.length} change(s) also rejected by server)`
              : `Sync failed: ${res.status}`,
            ...(allRejected.length > 0 ? { rejectedCount: allRejected.length } : {}),
          });
          return;
        }

        // CR-01 fix: parse through the real schema instead of an `as` cast — a
        // malformed response now throws (caught below) instead of silently
        // masquerading as a valid body.
        const body = SyncBatchResponseSchema.parse(await res.json());
        allRejected.push(...body.rejected);

        // 4. Apply conflict resolution — preserve local-only rooms.
        //
        // CR-04 design note: useProjectStore is a single-active-project store.
        // The server processes ALL projects in the batch and returns merged snapshots
        // for each in body.merged. We can only apply UI state for the ACTIVE project
        // because there is no in-memory store for non-active projects.
        //
        // For non-active projects, their server-merged state is committed to D1.
        // When the user switches to a non-active project, the app loads from server,
        // which is the correct post-merge state. This means D1 is authoritative for
        // non-active projects — no data loss occurs, only a local-state refresh lag
        // until the user switches to that project.
        //
        // The dequeue in step 5 removes ALL flushed records (including non-active
        // project changes), so those changes are not re-uploaded on the next sync.
        // Track the deeper fix (multi-project local store) as a follow-on item.
        if (body.merged && body.merged.length > 0) {
          const projectStore = useProjectStore.getState();
          // Build local project map for conflict resolver
          const localRooms = projectStore.rooms;
          const localProjectId = String(projectStore.activeProjectId ?? '');

          if (localProjectId) {
            const localProjectsMap = new Map<string, ProjectSnapshot>();
            // Build a minimal local snapshot for the resolver
            const localSnapshot: ProjectSnapshot = {
              id: localProjectId,
              name: '',
              lastModified: new Date().toISOString(),
              rooms: localRooms as ProjectSnapshot['rooms'],
              activeRoomId: null,
              safetyMargin: 0,
              projectItems: [],
              projectOverrides: {},
            };
            localProjectsMap.set(localProjectId, localSnapshot);

            const merged = mergeProjectsFromServer(localProjectsMap, body.merged);
            // Apply the merged rooms for the active project only.
            // Non-active projects: their merged state is persisted in D1 (server-authoritative).
            const activeProject = merged.find((p) => String(p.id) === localProjectId);
            if (activeProject) {
              projectStore.setRooms(activeProject.rooms as typeof localRooms);
              // WR-01 fix (36-REVIEW.md): also apply the server-authoritative
              // post-merge global layer, not just rooms — otherwise the in-memory
              // editor silently diverges from what is actually persisted
              // server-side, and the next debounced edit re-pushes stale local
              // values that can clobber a concurrent device's newer write (the
              // global layer has no baseVersion/OCC — see syncProtocol.ts doc
              // comments, "Open Question 2"). Un-instrumented setter — never
              // re-enqueues this server-origin data (see applyServerMergedGlobalLayer).
              projectStore.applyServerMergedGlobalLayer({
                projectItems: activeProject.projectItems,
                projectOverrides: activeProject.projectOverrides,
                safetyMargin: activeProject.safetyMargin,
                meta: activeProject.meta ?? {},
              });
            }
          }
        }

        // 5. Dequeue only the ids the server actually accepted (T-04-12 mitigation +
        // CR-01 fix: a rejected id must stay queued, never silently dequeued on a bare 200).
        const flushedIds = selectFlushedIds(chunk.map((r) => r.id), body.rejected);
        await dequeueFlushed(flushedIds);
      }

      // All chunks processed. pendingCount reflects whatever remains after partial
      // dequeues above (rejected records stay queued).
      set({ pendingCount: (await peekQueue()).length });

      if (allRejected.length > 0) {
        // CR-01: the pill must never show 'synced' while records remain queued.
        set({
          status: 'error',
          rejectedCount: allRejected.length,
          error: `${allRejected.length} change(s) rejected by server`,
        });
      } else {
        set({ status: 'synced', lastSyncedAt: Date.now(), error: null, rejectedCount: 0 });
      }
    } catch (e) {
      // Network / parse error — retain queue for retry
      const msg = e instanceof Error ? e.message : 'Unknown sync error';
      set({ status: 'error', error: msg });
    }
  },
}));
